import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight,
  Wallet,
  Share2,
  Smartphone,
  Target,
  Sparkles,
  GraduationCap,
  TrendingUp,
  Gift,
  Clock,
  CheckCircle2,
  ArrowRight,
  Zap,
} from "lucide-react";

// Hero carousel images
const HERO_IMAGES = [
  "/torta-hero-new.jpg",
  "/ocasion-lonchera.jpg",
  "/carrito-venta.jpg",
  "/unete-equipo-ventas.jpg",
  "/familia-compartiendo.jpg",
  "/torta-producto-real.jpg",
  "/promo-3mas1-new.jpg",
];

// Benefits data
const BENEFITS = [
  {
    icon: Wallet,
    title: "Comisiones semanales",
    desc: "Recibe tu pago cada semana directo a tu cuenta",
    color: "from-emerald-500/20 to-emerald-500/5",
    iconColor: "text-emerald-600",
  },
  {
    icon: Share2,
    title: "Catálogo listo",
    desc: "Comparte tu link y recibe pedidos automáticos",
    color: "from-blue-500/20 to-blue-500/5",
    iconColor: "text-blue-600",
  },
  {
    icon: Smartphone,
    title: "Pedidos digitales",
    desc: "Todo desde tu celular, sin complicaciones",
    color: "from-purple-500/20 to-purple-500/5",
    iconColor: "text-purple-600",
  },
  {
    icon: Gift,
    title: "Bonos por metas",
    desc: "Desbloquea premios al alcanzar tus objetivos",
    color: "from-amber-500/20 to-amber-500/5",
    iconColor: "text-amber-600",
  },
  {
    icon: Sparkles,
    title: "Sin experiencia",
    desc: "Te capacitamos desde cero para que vendas",
    color: "from-pink-500/20 to-pink-500/5",
    iconColor: "text-pink-600",
  },
  {
    icon: GraduationCap,
    title: "Soporte continuo",
    desc: "Siempre estaremos para ayudarte a crecer",
    color: "from-cyan-500/20 to-cyan-500/5",
    iconColor: "text-cyan-600",
  },
];

// Gamification tiers
const TIERS = [
  {
    level: "Nivel Inicial",
    badge: "medal-bronze",
    color: "from-amber-700 to-amber-600",
    borderColor: "border-amber-500/50",
    meta: "0 - 10 ventas/mes",
    bonus: "Comisión base",
    unlocked: true,
  },
  {
    level: "Nivel Pro",
    badge: "medal-silver",
    color: "from-slate-400 to-slate-300",
    borderColor: "border-slate-400/50",
    meta: "11 - 30 ventas/mes",
    bonus: "+$5.000/venta",
    unlocked: false,
  },
  {
    level: "Vendedor Elite",
    badge: "medal-gold",
    color: "from-yellow-400 to-amber-300",
    borderColor: "border-yellow-400/50",
    meta: "31+ ventas/mes",
    bonus: "+$10.000/venta + Bonos",
    unlocked: false,
  },
];

// Animated counter component
const AnimatedCounter = ({ value, prefix = "", suffix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * value));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, value, duration]);

  return (
    <span ref={countRef}>
      {prefix}{count.toLocaleString("es-CO")}{suffix}
    </span>
  );
};

// Floating coins animation
const FloatingCoins = () => {
  const coins = Array.from({ length: 6 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 3 + Math.random() * 2,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {coins.map((coin) => (
        <motion.div
          key={coin.id}
          className="absolute w-6 h-6 rounded-full bg-gradient-to-br from-yellow-300 to-amber-500 shadow-lg"
          style={{ left: `${coin.x}%` }}
          initial={{ y: "100vh", opacity: 0, scale: 0.5 }}
          animate={{
            y: "-20vh",
            opacity: [0, 1, 1, 0],
            scale: [0.5, 1, 1, 0.5],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: coin.duration,
            delay: coin.delay,
            repeat: Infinity,
            repeatDelay: 2,
            ease: "easeOut",
          }}
        >
          <span className="flex items-center justify-center h-full text-amber-800 font-bold text-xs">$</span>
        </motion.div>
      ))}
    </div>
  );
};

// Phone Mockup Component
const PhoneMockup = () => {
  return (
    <motion.div
      className="relative mx-auto w-[280px] sm:w-[320px]"
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: [0, -10, 0], opacity: 1 }}
      transition={{
        y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
        opacity: { duration: 0.5 },
      }}
    >
      {/* Phone frame */}
      <div className="relative bg-gray-900 rounded-[3rem] p-3 shadow-2xl shadow-black/30">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-7 bg-gray-900 rounded-b-2xl z-20" />
        
        {/* Screen */}
        <div className="bg-[#FBF5E8] rounded-[2.3rem] overflow-hidden">
          {/* Status bar */}
          <div className="bg-[#4A2C0A] px-6 py-3 pt-8 flex justify-between items-center">
            <span className="text-white/80 text-xs font-['Poppins']">9:41</span>
            <div className="flex gap-1">
              <div className="w-4 h-2 bg-white/80 rounded-sm" />
              <div className="w-3 h-2 bg-white/50 rounded-sm" />
            </div>
          </div>
          
          {/* App content */}
          <div className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C8902A] flex items-center justify-center">
                <span className="text-white text-lg">B</span>
              </div>
              <div>
                <p className="font-['Poppins'] text-xs text-[#4A2C0A]/60">Hola,</p>
                <p className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold">Maria Vendedora</p>
              </div>
            </div>
            
            {/* Stats card */}
            <div className="bg-gradient-to-br from-[#4A2C0A] to-[#6B4423] rounded-2xl p-4 text-white">
              <p className="text-xs text-white/70 font-['Poppins']">Ventas de hoy</p>
              <p className="font-['Playfair_Display'] text-3xl font-bold mt-1">$185.000</p>
              <div className="flex items-center gap-1 mt-2">
                <TrendingUp className="w-3 h-3 text-green-400" />
                <span className="text-xs text-green-400 font-['Poppins']">+23% vs ayer</span>
              </div>
            </div>
            
            {/* Commission */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#E5D4B3]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-[#4A2C0A]/60 font-['Poppins']">Tu comision</span>
                <span className="text-xs text-[#C8902A] font-['Poppins'] font-medium">Esta semana</span>
              </div>
              <p className="font-['Playfair_Display'] text-2xl font-bold text-[#4A2C0A]">$47.500</p>
            </div>
            
            {/* Progress bar */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#E5D4B3]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-[#4A2C0A]/60 font-['Poppins']">Meta semanal</span>
                <span className="text-xs text-[#4A2C0A] font-['Poppins'] font-medium">75%</span>
              </div>
              <div className="h-3 bg-[#E5D4B3] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#C8902A] to-[#E5A83A] rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: "75%" }}
                  transition={{ duration: 1.5, delay: 0.5 }}
                />
              </div>
              <p className="text-xs text-[#C8902A] font-['Poppins'] font-medium mt-2 text-center">
                Te faltan $20.000 para tu bono
              </p>
            </div>
            
            {/* Share button */}
            <motion.button
              className="w-full bg-[#C8902A] text-white font-['Poppins'] font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Share2 className="w-4 h-4" />
              Compartir catalogo
            </motion.button>
          </div>
        </div>
      </div>
      
      {/* Glow effect */}
      <div className="absolute inset-0 -z-10 blur-3xl bg-[#C8902A]/20 rounded-full transform scale-110" />
    </motion.div>
  );
};

// Medal badge component
const MedalBadge = ({ type }) => {
  const colors = {
    "medal-bronze": "from-amber-700 via-amber-500 to-amber-700",
    "medal-silver": "from-slate-400 via-slate-200 to-slate-400",
    "medal-gold": "from-yellow-400 via-yellow-200 to-yellow-400",
  };

  const emojis = {
    "medal-bronze": "🥉",
    "medal-silver": "🥈",
    "medal-gold": "🥇",
  };

  return (
    <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${colors[type]} flex items-center justify-center shadow-lg`}>
      <span className="text-2xl">{emojis[type]}</span>
    </div>
  );
};

export default function RegistroLanding() {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF5E8] overflow-x-hidden">
      {/* HERO SECTION */}
      <section className="relative min-h-[100vh] flex flex-col">
        {/* Background carousel */}
        <div className="absolute inset-0 z-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentImageIndex}
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
            >
              <img
                src={HERO_IMAGES[currentImageIndex]}
                alt=""
                className="w-full h-full object-cover"
              />
            </motion.div>
          </AnimatePresence>
          {/* Warm gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#4A2C0A]/80 via-[#4A2C0A]/60 to-[#FBF5E8]" />
          {/* Blur overlay at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FBF5E8] to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex-1 flex flex-col">
          {/* Logo */}
          <div className="p-4 flex justify-center">
            <img
              src="/logo-torta-cuajada.png"
              alt="Baruj Shefa"
              className="h-16 w-16 rounded-full border-2 border-white/20 shadow-lg"
            />
          </div>

          {/* Hero text */}
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 mb-6"
            >
              <Zap className="w-4 h-4 text-[#C8902A]" />
              <span className="text-sm font-medium text-white font-['Poppins']">
                El sabor mas querido de Chipaque, Cundinamarca
              </span>
            </motion.div>

            {/* Main title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-tight mb-4"
            >
              Gana dinero<br />
              <span className="text-[#C8902A]">vendiendo tradicion</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="font-['Poppins'] text-white/80 text-base sm:text-lg max-w-md mb-8"
            >
              Unete a la red de vendedores Baruj Shefa y gana comisiones compartiendo nuestro catalogo digital.
            </motion.p>

            {/* CTA Button - Hero */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <Button
                onClick={() => navigate("/registro-vendedor/acceso")}
                className="h-14 px-8 text-lg font-semibold bg-[#C8902A] hover:bg-[#B07D24] text-white rounded-full shadow-lg shadow-[#C8902A]/40 font-['Poppins'] transition-all hover:scale-105 active:scale-95"
                data-testid="hero-cta-btn"
              >
                QUIERO VENDER
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            className="pb-8 flex justify-center"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
              <div className="w-1 h-3 bg-white/50 rounded-full" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* PHONE MOCKUP SECTION */}
      <section className="relative py-16 px-6 bg-gradient-to-b from-[#FBF5E8] to-white overflow-hidden">
        {/* Section title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#4A2C0A] font-bold mb-4">
            Tu negocio en la palma<br />de tu mano
          </h2>
          <p className="font-['Poppins'] text-[#4A2C0A]/70 max-w-md mx-auto">
            Dashboard intuitivo para controlar tus ventas, comisiones y metas desde cualquier lugar.
          </p>
        </motion.div>

        {/* Phone mockup */}
        <PhoneMockup />

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-12 grid grid-cols-3 gap-4 max-w-md mx-auto"
        >
          <div className="text-center">
            <p className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#4A2C0A]">
              <AnimatedCounter value={50} suffix="+" />
            </p>
            <p className="font-['Poppins'] text-xs text-[#4A2C0A]/60">Vendedores</p>
          </div>
          <div className="text-center">
            <p className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#C8902A]">
              $<AnimatedCounter value={2} suffix="M" />
            </p>
            <p className="font-['Poppins'] text-xs text-[#4A2C0A]/60">Comisiones pagadas</p>
          </div>
          <div className="text-center">
            <p className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#4A2C0A]">
              <AnimatedCounter value={24} suffix="h" />
            </p>
            <p className="font-['Poppins'] text-xs text-[#4A2C0A]/60">Aprobacion</p>
          </div>
        </motion.div>
      </section>

      {/* BENEFITS SECTION */}
      <section className="py-16 px-6 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#4A2C0A] font-bold mb-4">
            Por que unirte a<br /><span className="text-[#C8902A]">Baruj Shefa</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
          {BENEFITS.map((benefit, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`bg-gradient-to-br ${benefit.color} rounded-2xl p-5 border border-[#E5D4B3]/50 shadow-[0_8px_30px_rgb(74,44,10,0.08)] hover:shadow-lg transition-all hover:-translate-y-1`}
              data-testid={`benefit-card-${i}`}
            >
              <div className={`w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center mb-3`}>
                <benefit.icon className={`w-6 h-6 ${benefit.iconColor}`} />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#4A2C0A] mb-1">
                {benefit.title}
              </h3>
              <p className="font-['Poppins'] text-sm text-[#4A2C0A]/70">
                {benefit.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* GAMIFICATION SECTION */}
      <section className="relative py-16 px-6 bg-gradient-to-b from-[#4A2C0A] to-[#2D1A06] overflow-hidden">
        {/* Floating coins */}
        <FloatingCoins />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 relative z-10"
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-white font-bold mb-4">
            Sube de nivel,<br /><span className="text-[#C8902A]">gana mas</span>
          </h2>
          <p className="font-['Poppins'] text-white/70 max-w-md mx-auto">
            Mientras mas vendas, mejores beneficios desbloqueas. Tu esfuerzo tiene recompensa.
          </p>
        </motion.div>

        {/* Tiers */}
        <div className="space-y-4 max-w-md mx-auto relative z-10">
          {TIERS.map((tier, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className={`relative bg-white/10 backdrop-blur-md rounded-2xl p-5 border ${tier.borderColor} ${
                !tier.unlocked ? "opacity-70" : ""
              }`}
              data-testid={`tier-card-${i}`}
            >
              <div className="flex items-center gap-4">
                <MedalBadge type={tier.badge} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-['Playfair_Display'] text-lg font-semibold text-white">
                      {tier.level}
                    </h3>
                    {tier.unlocked ? (
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                    ) : (
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full text-white/70 font-['Poppins']">
                        Bloqueado
                      </span>
                    )}
                  </div>
                  <p className="font-['Poppins'] text-sm text-white/60">{tier.meta}</p>
                </div>
                <div className="text-right">
                  <p className="font-['Poppins'] text-sm font-semibold text-[#C8902A]">
                    {tier.bonus}
                  </p>
                </div>
              </div>

              {/* Progress shine effect for unlocked */}
              {tier.unlocked && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent rounded-2xl"
                  animate={{ x: ["-100%", "100%"] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                />
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-20 px-6 bg-[#FBF5E8] text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-md mx-auto"
        >
          <div className="w-20 h-20 bg-[#C8902A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Smartphone className="w-10 h-10 text-[#C8902A]" />
          </div>
          
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#4A2C0A] font-bold mb-4">
            Empieza a ganar<br />hoy mismo
          </h2>
          
          <p className="font-['Poppins'] text-[#4A2C0A]/70 mb-8">
            Sin inversion inicial. Sin experiencia previa. Solo necesitas tu celular y ganas de crecer.
          </p>

          <Button
            onClick={() => navigate("/registro-vendedor/acceso")}
            className="w-full h-16 text-xl font-bold bg-[#C8902A] hover:bg-[#B07D24] text-white rounded-2xl shadow-xl shadow-[#C8902A]/30 font-['Poppins'] transition-all hover:scale-[1.02] active:scale-95"
            data-testid="final-cta-btn"
          >
            QUIERO VENDER
            <ChevronRight className="w-6 h-6 ml-2" />
          </Button>
          
          <p className="font-['Poppins'] text-sm text-[#4A2C0A]/50 mt-4">
            Empieza hoy mismo desde tu celular
          </p>

          {/* Already have account */}
          <Link
            to="/login"
            className="inline-block mt-6 font-['Poppins'] text-[#4A2C0A]/60 hover:text-[#4A2C0A] underline-offset-4 hover:underline transition-colors"
            data-testid="login-link"
          >
            Ya tengo cuenta
          </Link>
        </motion.div>
      </section>

      {/* STICKY BOTTOM CTA (Mobile) */}
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-xl border-t border-[#E5D4B3] z-50 md:hidden"
      >
        <Button
          onClick={() => navigate("/registro-vendedor/acceso")}
          className="w-full h-14 text-lg font-bold bg-[#C8902A] hover:bg-[#B07D24] text-white rounded-xl shadow-lg shadow-[#C8902A]/30 font-['Poppins']"
          data-testid="sticky-cta-btn"
        >
          QUIERO VENDER
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </motion.div>

      {/* Bottom padding for sticky CTA */}
      <div className="h-20 md:hidden" />
    </div>
  );
}
